import { FC, useCallback, useEffect, useMemo } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useTranslations } from 'hooks/useTranslations';
import { getCustomerEmail } from 'utils/customerUtils';
import { Customer } from 'models/customer';

import InputField from 'components/UI/InputField';
import Button from 'components/UI/Buttons/Button';
import Loader from 'components/UI/Loader';
import Modal from 'components/UI/Modal';

import { ModalDescription, ModalForm, ModalTitle } from 'styles';
import { ButtonGroup, LoaderWrapper } from './styled';

export type ChangeEmailValues = {
  email: string;
  password: string;
};

type Props = {
  customer: Customer;
  isLoading: boolean;
  isCompleted: boolean;
  isOpen: boolean;
  isBusiness?: boolean;
  close: () => void;
  submit: (values: ChangeEmailValues) => void;
};

const ChangeEmailModal: FC<Props> = ({
  customer,
  isLoading,
  isCompleted,
  isOpen,
  isBusiness,
  close,
  submit
}) => {
  const t = useTranslations();

  const currentEmail = isBusiness
    ? customer.attributes.primaryContact?.emailAddress || ''
    : getCustomerEmail(customer.attributes.eContact);

  // Zod schema
  const schema = useMemo(
    () =>
      z.object({
        email: z
          .email('input.email_invalid')
          .min(1, 'input.email_required')
          .refine((value) => value.toLowerCase() !== currentEmail, {
            message: t('change_email.request.email_same', {
              email: currentEmail
            })
          }),
        password: z.string().min(1, 'input.password_required')
      }),
    [currentEmail, t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    resetField
  } = useForm({
    resolver: zodResolver(schema)
  });

  // Close if completed
  useEffect(() => {
    if (isCompleted) {
      close();
    }
  }, [close, isCompleted]);

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values) =>
      submit({
        email: values.email,
        password: values.password
      }),
    [submit]
  );

  // Form
  const form = useMemo(() => {
    if (isLoading) {
      return (
        <LoaderWrapper>
          <Loader center />
        </LoaderWrapper>
      );
    }
    return (
      <>
        <InputField
          name="email"
          description="input.email_label"
          error={errors.email}
          register={register('email')}
          reset={resetField}
          outlined
        />
        <InputField
          name="password"
          description="input.password_label"
          type="password"
          error={errors.password}
          register={register('password')}
          reset={resetField}
          outlined
        />
      </>
    );
  }, [register, resetField, errors, isLoading]);

  return (
    <Modal isOpen={isOpen}>
      <ModalTitle>
        {t(
          isBusiness
            ? 'change_email.request.primary_contact_title'
            : 'change_email.request.title'
        )}
      </ModalTitle>
      <ModalDescription>
        {t(
          isBusiness
            ? 'change_email.request.primary_contact_description'
            : 'change_email.request.description'
        )}
      </ModalDescription>
      <ModalForm onSubmit={handleSubmit(onSubmit)}>
        {form}
        <ButtonGroup>
          <Button color="transparent" textColor="primary" onClick={close}>
            {t('change_email.request.cancel')}
          </Button>
          <Button submit disabled={isLoading}>
            {t('change_email.request.confirm')}
          </Button>
        </ButtonGroup>
      </ModalForm>
    </Modal>
  );
};

export default ChangeEmailModal;
